/**
 * ImpersonationBanner
 *
 * Sticky red banner shown at the top of every page when the active
 * Firebase session is an admin "Sign in as customer" session.
 *
 * Goals:
 *   - Never let an operator forget they're impersonating. The banner
 *     is unmissable: full width, deep red gradient, white text.
 *   - Show *who* the operator is, *as whom* they're acting, *why*,
 *     and *how long* until the session naturally expires.
 *   - Provide a single "End impersonation" button that calls
 *     AuthContext.endImpersonation() — backend audit log + sign out.
 *   - Self-update the countdown every second so the operator sees
 *     the window closing and can renew (re-trigger from the Hub) or
 *     wrap up.
 *
 * Renders nothing when not impersonating.
 *
 * Mounted in Layout (and any other top-level container that should
 * carry the warning, e.g. the public landing layout).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import { useAuth } from '../contexts/AuthContext';

/**
 * CSS variable name the banner publishes its measured height under.
 * Anything else in the app that needs to push down by the banner's
 * height (the AppBar, the main-content top padding, etc.) reads
 * this via `var(--imp-banner-height, 0px)`. Cleared to 0px when not
 * impersonating so the rest of the layout doesn't carry stray
 * offsets.
 */
const BANNER_HEIGHT_VAR = '--imp-banner-height';

/**
 * Format a remaining-seconds count as `MM:SS` or `Hh MMm` for ≥1h.
 */
function formatRemaining(seconds: number): string {
  if (seconds <= 0) return 'expiring';
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m < 60) return `${m}m ${s.toString().padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}

const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, impersonationClaims, endImpersonation } = useAuth();
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [ending, setEnding] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  // 1Hz tick for the countdown. Cheap; we only render when the
  // banner is up.
  useEffect(() => {
    if (!isImpersonating) return;
    const id = window.setInterval(() => {
      setNow(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [isImpersonating]);

  // Publish the banner's measured height as a CSS variable on the
  // document root, so the AppBar and main-content can offset
  // themselves by exactly that amount (the banner is taller on
  // mobile because the inner Stack flips to column orientation).
  // Cleared to 0px when the banner is unmounted / hidden.
  useEffect(() => {
    if (!isImpersonating) {
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, '0px');
      return;
    }
    const node = bannerRef.current;
    if (!node) return;
    const publish = (h: number) => {
      document.documentElement.style.setProperty(
        BANNER_HEIGHT_VAR,
        `${Math.round(h)}px`
      );
    };
    publish(node.getBoundingClientRect().height);
    // ResizeObserver handles responsive flips (xs column → md row)
    // and any future content changes (countdown text width, etc.)
    // without requiring a hard-coded height table.
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        publish(entry.contentRect.height);
      }
    });
    ro.observe(node);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, '0px');
    };
  }, [isImpersonating]);

  const remaining = useMemo(() => {
    const exp = impersonationClaims?.expiresAt ?? null;
    if (!exp) return null;
    return Math.max(0, exp - now);
  }, [impersonationClaims?.expiresAt, now]);

  if (!isImpersonating || !impersonationClaims) return null;

  const handleEnd = async () => {
    setEnding(true);
    try {
      await endImpersonation();
    } finally {
      setEnding(false);
    }
  };

  // Past the encoded expiry (unusual — backend dep stops treating
  // the request as impersonation, but the claim block is still in
  // the token until natural Firebase refresh). Visually demote.
  const expired = remaining !== null && remaining <= 0;

  return (
    <Box
      ref={bannerRef}
      role="alert"
      sx={{
        // Pinned to the viewport top. The AppBar and main content
        // are pushed down by `--imp-banner-height` (published from
        // the effect above), so this never covers the nav bar.
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        // Above the AppBar (theme.zIndex.appBar = 1100) and the
        // permanent Drawer (theme.zIndex.drawer = 1200) so the
        // banner is the visually-top element on the page.
        zIndex: (theme) => theme.zIndex.drawer + 2,
        // Bright, unmissable banner. Use a gradient that's clearly
        // distinct from the Passio brand gradient so the operator
        // never confuses "impersonating" with "normal admin".
        background: expired
          ? 'linear-gradient(90deg, #6b6b6b 0%, #444 100%)'
          : 'linear-gradient(90deg, #b91c1c 0%, #7f1d1d 100%)',
        color: '#fff',
        py: 1,
        px: 2,
        boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1, md: 2 }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <VpnLockIcon fontSize="small" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {expired
                ? 'Impersonation window expired'
                : 'Impersonating customer'}
              {' · '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                {impersonationClaims.targetCustomerTag ||
                  impersonationClaims.targetCustomerId}
              </Box>
            </Typography>
            <Typography
              variant="caption"
              sx={{
                lineHeight: 1.3,
                opacity: 0.9,
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {impersonationClaims.impersonatedByEmail ||
                impersonationClaims.impersonatedByTag ||
                impersonationClaims.impersonatedBy}
              {impersonationClaims.reason && (
                <>
                  {' · '}
                  <Tooltip title={impersonationClaims.reason}>
                    <span>reason: {impersonationClaims.reason.slice(0, 80)}</span>
                  </Tooltip>
                </>
              )}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          {remaining !== null && (
            <Tooltip title="Time remaining before the impersonation claims expire. After this point the session reverts to a normal customer session.">
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatRemaining(remaining)} left
              </Typography>
            </Tooltip>
          )}
          <Button
            variant="contained"
            size="small"
            color="inherit"
            startIcon={<LogoutIcon />}
            disabled={ending}
            onClick={handleEnd}
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
            }}
          >
            {ending ? 'Ending…' : 'End impersonation'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ImpersonationBanner;
